import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { BxBadgeComponent, BxCardComponent, BxCardHeaderComponent } from '@beyonnex/bx-angular-ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AgGridAngular } from 'ag-grid-angular';
import type { ColDef, GetRowIdParams, GridApi, GridReadyEvent, ICellRendererParams, ValueGetterParams } from 'ag-grid-enterprise';
import { ROUTES } from '../../../app.models';
import { LinkRendererComponent } from '../../../components/ditto-grid/custom-renderer/link-renderer/link-renderer.component';
import { ProductStatusRendererComponent } from '../../../components/product-status-badge/product-status-renderer/product-status-renderer.component';
import type { LinkData } from '../../../models/core.models';
import { CommonTranslateService } from '../../../services/common-translate/common-translate.service';
import { DittoGridService } from '../../../services/grid/ditto-grid.service';
import { LocalizationService } from '../../../services/localization/localization.service';
import { NotificationsService } from '../../../services/notifications/notifications.service';
import { PropertyStatusService } from '../../../services/property-status/property-status.service';
import { PROPERTY_STATUS_SEVERITY } from '../../../services/property-status/property-status.service.models';
import type { Product, ProductSeverity, PropertyStatus } from '../../../services/property-status/property-status.service.models';
import { ProductFilterComponent } from './product-filter/product-filter.component';
import { PropertyOverviewDetailRendererComponent } from './property-overview-detail-renderer/property-overview-detail-renderer.component';
import { PropertyOverviewOverlayComponent } from './property-overview-overlay/property-overview-overlay.component';
import { PropertyOverviewDatasource } from './property-overview.datasource';
import { PRODUCT_COLUMNS, PROPERTY_OVERVIEW_PAGE_SIZE } from './property-overview.models';
import type { PropertyOverviewOutcome, PropertyOverviewOverlayInputs } from './property-overview.models';

@Component({
  selector: 'bxi-property-overview',
  imports: [AgGridAngular, BxBadgeComponent, BxCardComponent, BxCardHeaderComponent, ProductFilterComponent, TranslatePipe],
  templateUrl: './property-overview.component.html',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyOverviewComponent {
  private readonly translate = inject(TranslateService);
  private readonly commonTranslate = inject(CommonTranslateService);
  private readonly localization = inject(LocalizationService);
  private readonly notifications = inject(NotificationsService);
  private readonly propertyStatusService = inject(PropertyStatusService);
  protected readonly dittoGridService = inject(DittoGridService);

  protected readonly pageSize = PROPERTY_OVERVIEW_PAGE_SIZE;

  private readonly outcome = signal<PropertyOverviewOutcome | undefined>(undefined);

  protected readonly countLabel = computed(() => {
    const outcome = this.outcome();

    // A failed request has no total, so the badge clears rather than keeping a count the rows contradict.
    return outcome?.status === 'loaded'
      ? this.translate.instant('product-view.property-overview.count', { total: outcome.total })
      : undefined;
  });

  private readonly products = signal<Product[]>([]);

  protected readonly datasource = new PropertyOverviewDatasource(
    this.propertyStatusService,
    (outcome) => this.handleOutcome(outcome),
    () => this.products(),
  );

  /** Absent while a request is in flight or once rows arrive, which is what lets AG Grid's loading overlay show. */
  protected readonly overlayInputs = computed<PropertyOverviewOverlayInputs | undefined>(() => {
    const outcome = this.outcome();
    if (!outcome || (outcome.status === 'loaded' && outcome.total > 0)) {
      return undefined;
    }

    return { outcome, filtered: this.products().length > 0, retry: this.retry };
  });

  protected readonly activeOverlay = computed(() => (this.overlayInputs() ? PropertyOverviewOverlayComponent : undefined));

  // An arrow field so the reference stays stable across recomputes and never re-triggers the overlay refresh.
  private readonly retry = (): void => {
    this.gridApi?.refreshServerSide({ purge: true });
  };

  private handleOutcome(outcome?: PropertyOverviewOutcome): void {
    this.outcome.set(outcome);
    if (outcome?.status !== 'failed') {
      return;
    }

    // The overlay states the same thing durably; this is the alert that something changed.
    const keys = outcome.denied ? ERROR_KEYS.denied : ERROR_KEYS.failed;
    this.notifications.addErrorNotification(this.translate.instant(keys.title), this.translate.instant(keys.message));
  }

  protected readonly columnDefs: ColDef<PropertyStatus>[] = [
    {
      field: 'billingUnitNumber',
      headerName: this.translate.instant('product-view.property-overview.billingUnit'),
      // agGroupCellRenderer prepends the expand chevron and renders the link as its inner content.
      cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: ({ data }: ICellRendererParams<PropertyStatus>): BillingUnitCellRendererParams => ({
        suppressCount: true,
        innerRenderer: LinkRendererComponent,
        linkData: data && { value: data.billingUnitNumber, href: `/${ROUTES.PROPERTY_DETAILS}?thingId=${data.thingId}` },
      }),
    },
    {
      ...CENTERED_COLUMN,
      colId: 'overallStatus',
      headerName: this.translate.instant('product-view.property-overview.overallStatus'),
      // The property level carries a status name rather than a number, so it resolves to one here.
      valueGetter: ({ data }: ValueGetterParams<PropertyStatus>): ProductSeverity | undefined =>
        data && PROPERTY_STATUS_SEVERITY[data.overallStatus],
      cellRenderer: ProductStatusRendererComponent,
    },
    ...PRODUCT_COLUMNS.map((product): ColDef<PropertyStatus> => ({
      ...CENTERED_COLUMN,
      colId: product,
      headerName: this.commonTranslate.translateProductAbbreviation(product),
      // `products` lists only what the billing unit serves, so a miss is the not-served dash.
      valueGetter: ({ data }: ValueGetterParams<PropertyStatus>): ProductSeverity | undefined =>
        data?.products.find((entry) => entry.product === product)?.severity,
      cellRenderer: ProductStatusRendererComponent,
      cellRendererParams: { iconOnly: true },
    })),
  ];

  // Sorting and filtering stay off until the API supports them: it only orders by error rate
  // descending, and an affordance that silently reordered the current page would mislead.
  protected readonly defaultColDef: ColDef = {
    flex: 1,
    minWidth: 120,
    sortable: false,
    filter: false,
    suppressHeaderMenuButton: true,
  };

  // Localizes AG Grid's built-in strings, the pagination panel among them.
  protected readonly locale = this.localization.getAgGridLocale();

  protected readonly detailRenderer = PropertyOverviewDetailRendererComponent;

  // A property serving no product has nothing to break down, so it stays flat.
  protected readonly isRowMaster = (data: PropertyStatus): boolean => data.products.length > 0;

  // Expansion state is keyed by row id, and the server-side model otherwise numbers rows by position —
  // without this an expanded row would collapse as soon as the pager reloaded its block.
  protected readonly getRowId = ({ data }: GetRowIdParams<PropertyStatus>): string => data.billingUnitNumber;

  private gridApi?: GridApi<PropertyStatus>;

  protected handleGridReady({ api }: GridReadyEvent<PropertyStatus>): void {
    this.gridApi = api;
  }

  protected handleProductsChange(products: Product[]): void {
    this.products.set(products);
    // Purging rebuilds the cache from block 0, which collapses the row count and so drops the pager back to page 1.
    this.gridApi?.refreshServerSide({ purge: true });
  }
}

/** The same keys the overlay renders, so the toast and the grid body never word the failure differently. */
const ERROR_KEYS = {
  denied: {
    title: 'product-view.property-overview.error.denied.title',
    message: 'product-view.property-overview.error.denied.message',
  },
  failed: {
    title: 'product-view.property-overview.error.failed.title',
    message: 'product-view.property-overview.error.failed.message',
  },
};

/** The status columns centre their badge under a centred header; the billing unit stays left-aligned. */
const CENTERED_COLUMN: ColDef<PropertyStatus> = { cellClass: 'bxi-grid-cell-center', headerClass: 'bxi-grid-header-center' };

interface BillingUnitCellRendererParams {
  suppressCount: boolean;
  innerRenderer: typeof LinkRendererComponent;
  linkData?: LinkData;
}
